'use client';

import React, { memo } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

type BadgeColor = 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'pink';

interface BadgeProps {
  children: React.ReactNode;
  color?: BadgeColor;
  showDot?: boolean;
}

const colorClasses: Record<
  BadgeColor,
  {
    light: { bg: string; text: string; dot: string };
    dark: { bg: string; text: string; dot: string };
  }
> = {
  gray: {
    light: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'fill-gray-400' },
    dark: { bg: 'bg-gray-400/10', text: 'text-gray-400', dot: 'fill-gray-500' },
  },
  red: {
    light: { bg: 'bg-red-100', text: 'text-red-700', dot: 'fill-red-500' },
    dark: { bg: 'bg-red-400/10', text: 'text-red-400', dot: 'fill-red-400' },
  },
  yellow: {
    light: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'fill-yellow-500' },
    dark: { bg: 'bg-yellow-400/10', text: 'text-yellow-500', dot: 'fill-yellow-500' },
  },
  green: {
    light: { bg: 'bg-green-100', text: 'text-green-700', dot: 'fill-green-500' },
    dark: { bg: 'bg-green-400/10', text: 'text-green-400', dot: 'fill-green-400' },
  },
  blue: {
    light: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'fill-blue-500' },
    dark: { bg: 'bg-blue-400/10', text: 'text-blue-400', dot: 'fill-blue-400' },
  },
  indigo: {
    light: { bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'fill-indigo-500' },
    dark: { bg: 'bg-indigo-400/10', text: 'text-indigo-400', dot: 'fill-indigo-400' },
  },
  purple: {
    light: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'fill-purple-500' },
    dark: { bg: 'bg-purple-400/10', text: 'text-purple-400', dot: 'fill-purple-400' },
  },
  pink: {
    light: { bg: 'bg-pink-100', text: 'text-pink-700', dot: 'fill-pink-500' },
    dark: { bg: 'bg-pink-400/10', text: 'text-pink-400', dot: 'fill-pink-400' },
  },
};

const Badge = memo(function Badge({ children, color = 'gray', showDot = true }: BadgeProps) {
  const { darkMode } = useDarkMode();
  const colors = darkMode ? colorClasses[color].dark : colorClasses[color].light;

  return (
    <span
      className={`inline-flex items-center gap-x-1.5 rounded-full px-1.5 py-0.5 text-xs font-medium capitalize ${colors.bg} ${colors.text}`}
    >
      {showDot && (
        <svg viewBox="0 0 6 6" aria-hidden="true" className={`size-1.5 ${colors.dot}`}>
          <circle r={3} cx={3} cy={3} />
        </svg>
      )}
      {children}
    </span>
  );
});

export default Badge;
